import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { useReactToPrint } from "react-to-print";
import { downloadCotizacionPdf, printCotizacionPdf } from "@/lib/pdf";
import { Plus, Trash2, Printer, CheckCircle2, Home, ChevronDown, Download } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { money, buildIdentificador, fechaMX } from "@/lib/format";

export default function Cotizador() {
  const [step, setStep] = useState(1);
  const [clientes, setClientes] = useState([]);
  const [listas, setListas] = useState([]);
  const [selectedClienteId, setSelectedClienteId] = useState("");
  const [listaPrecios, setListaPrecios] = useState("");
  const [condicionPago, setCondicionPago] = useState("");
  const [vigencia, setVigencia] = useState("15 días");
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [checkedIds, setCheckedIds] = useState([]);
  const [partidas, setPartidas] = useState([]);
  const [descuentos, setDescuentos] = useState([]);
  const [cotNo, setCotNo] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const printRef = useRef(null);
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get("editar");
  const [editCot, setEditCot] = useState(null);
  const editAplicado = useRef(false);

  const selectedCliente = clientes.find((c) => c.id === selectedClienteId);

  useEffect(() => {
    Promise.all([api.get("/clientes"), api.get("/price-lists")]).then(([c, l]) => {
      setClientes(c.data || []); setListas(l.data || []);
    });
  }, []);

  useEffect(() => {
    if (!editId) return;
    api.get(`/cotizaciones/${editId}`).then(({ data }) => {
      if (!["Pendiente de autorización", "Rechazada"].includes(data.estatus)) {
        toast.error("Esta cotización ya no se puede editar"); navigate("/cotizaciones"); return;
      }
      editAplicado.current = false;
      setEditCot(data); setCotNo(data.cot_no);
      setPartidas((data.partidas || []).map((p, i) => ({ ...p, id: p.id || `e${i}` })));
      setSelectedClienteId(data.cliente_id || "");
    }).catch((err) => { toast.error(err?.response?.data?.detail || "No se pudo cargar la cotización"); navigate("/cotizaciones"); });
  }, [editId]);

  useEffect(() => {
    if (!selectedCliente) return;
    setListaPrecios(selectedCliente.lista_precios || listas[0]?.id || "");
    const ds = Array.isArray(selectedCliente.descuentos) ? selectedCliente.descuentos.filter((d) => !d.isBase) : [];
    const base = ds.map((d, i) => ({ id: `d${i}`, nombre: d.tipoLabel || d.nombre || "Descuento", valor: Number(d.porcentaje || d.valor || 0), estado: "activo" }));
    if (editCot && !editAplicado.current && editCot.cliente_id === selectedClienteId) {
      // Edición: respeta condiciones y descuentos guardados en la cotización.
      editAplicado.current = true;
      setCondicionPago(editCot.condicion_pago || "");
      setVigencia(editCot.vigencia || "15 días");
      const apl = editCot.descuentos_aplicados || [];
      const key = (d) => `${d.nombre}|${Number(d.valor)}`;
      const aplKeys = new Set(apl.map(key));
      const merged = base.map((d) => ({ ...d, estado: aplKeys.has(key(d)) ? "activo" : "suspendido" }));
      const extra = apl.filter((d) => !merged.some((m) => key(m) === key(d))).map((d, i) => ({ id: `x${i}`, nombre: d.nombre, valor: Number(d.valor), estado: "activo" }));
      setDescuentos([...merged, ...extra]);
      return;
    }
    if (editCot) { setDescuentos(base); return; }
    setCondicionPago(selectedCliente.condiciones_pago || "");
    if (selectedCliente.vigencia_cotizacion) setVigencia(selectedCliente.vigencia_cotizacion);
    setDescuentos(base);
  }, [selectedClienteId, listas]);

  useEffect(() => {
    if (!listaPrecios) { setAvailableProducts([]); return; }
    api.get(`/price-lists/${listaPrecios}/products`).then(({ data }) => {
      setAvailableProducts((data || []).map((p) => ({ product_id: p.product_id, nRef: p.n_ref, modelo: p.modelo, peso: Number(p.peso_unitario_kg || 0), precioUnitario: Number(p.precio_unitario || 0) })));
    });
  }, [listaPrecios]);

  const orderIndex = (pid) => { const i = availableProducts.findIndex((p) => p.product_id === pid); return i === -1 ? Number.MAX_SAFE_INTEGER : i; };
  const addPartidas = (prods) => {
    if (!prods.length) return;
    const base = Date.now();
    setPartidas((prev) => {
      const nuevos = prods.map((prod, idx) => ({
        id: `${base}-${idx}`, product_id: prod.product_id, nRef: prod.nRef, modelo: prod.modelo, tipo: "Verde",
        cantidad: 1, atados: 1 / 10, peso: prod.peso, pesoUnitario: prod.peso, precioUnitario: prod.precioUnitario,
      }));
      return [...prev, ...nuevos].sort((a, b) => orderIndex(a.product_id) - orderIndex(b.product_id));
    });
  };

  const toggleChecked = (id) => setCheckedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const allChecked = availableProducts.length > 0 && checkedIds.length === availableProducts.length;
  const toggleAll = () => setCheckedIds(allChecked ? [] : availableProducts.map((p) => p.product_id));
  const confirmAddSelected = () => {
    const seleccion = availableProducts.filter((p) => checkedIds.includes(p.product_id));
    if (!seleccion.length) return toast.error("Seleccione al menos un producto");
    const existentes = new Set(partidas.map((p) => p.product_id));
    const dup = seleccion.filter((p) => existentes.has(p.product_id));
    const nuevos = seleccion.filter((p) => !existentes.has(p.product_id));
    if (dup.length) toast.error(`Ya en la lista: ${dup.map((p) => p.modelo).join(", ")}`);
    if (nuevos.length) {
      addPartidas(nuevos);
      toast.success(`${nuevos.length} producto(s) agregado(s)`);
      setCheckedIds([]);
      setPickerOpen(false);
    }
  };

  const updatePartida = (id, field, value) => {
    setPartidas((prev) => prev.map((p) => {
      if (p.id !== id) return p;
      const up = { ...p, [field]: value };
      if (field === "cantidad") {
        const c = Number(value) || 0;
        up.peso = c * (Number(p.pesoUnitario) || 0);
        up.atados = c / 10;
      }
      return up;
    }));
  };
  const removePartida = (id) => setPartidas((prev) => prev.filter((p) => p.id !== id));
  const toggleDescuento = (id, checked) => setDescuentos((prev) => prev.map((d) => (d.id === id ? { ...d, estado: checked ? "activo" : "suspendido" } : d)));

  const subtotal = partidas.reduce((a, p) => a + Number(p.cantidad) * Number(p.precioUnitario), 0);
  const activeDiscounts = descuentos.filter((d) => d.estado === "activo");
  let curr = subtotal;
  activeDiscounts.forEach((d) => { curr -= curr * (Number(d.valor) / 100); });
  const totalDescuento = subtotal - curr;
  const subtotalConDescuento = Math.max(0, subtotal - totalDescuento);
  const iva = subtotalConDescuento * 0.16;
  const total = subtotalConDescuento + iva;
  const totalKg = partidas.reduce((a, p) => a + (Number(p.peso) || 0), 0);
  const fmtAtados = (v) => { const n = Number(v) || 0; return n % 1 === 0 ? String(n) : n.toFixed(2); };
  const cotFactor = subtotal > 0 ? subtotalConDescuento / subtotal : 1;
  const identificador = buildIdentificador({ fecha: new Date().toISOString().slice(0, 10), subtotalConDescuento, totalKg, listaNombre: listas.find((l) => l.id === listaPrecios)?.name || "" });
  const dinero = (v) => "$" + (Number(v) || 0).toFixed(2);
  const dirCliente = selectedCliente ? `${selectedCliente.calle || ""} ${selectedCliente.no_exterior || ""}${selectedCliente.no_interior ? " Int. " + selectedCliente.no_interior : ""}, ${selectedCliente.colonia || ""}, ${selectedCliente.municipio_alcaldia || ""}, ${selectedCliente.estado || ""} ${selectedCliente.codigo_postal || ""}`.replace(/\s+/g, " ").trim() : "";
  const totalAtados = partidas.reduce((a, p) => a + (Number(p.atados) || 0), 0);

  const goStep4 = async () => {
    if (partidas.length === 0) return toast.error("Agregue al menos una partida");
    if (!cotNo) {
      const { data } = await api.get("/cotizaciones/next-cot-no");
      setCotNo(data.cot_no);
    }
    setStep(4);
  };

  const guardar = async () => {
    if (!selectedCliente || partidas.length === 0 || !cotNo) return toast.error("Faltan datos para guardar la cotización");
    setSaving(true);
    try {
      if (editCot) {
        const { data } = await api.put(`/cotizaciones/${editCot.id}`, {
          condicion_pago: condicionPago, vigencia, partidas, descuentos_aplicados: activeDiscounts, subtotal: subtotalConDescuento, iva, total,
        });
        setEditCot(data); setSaved(true);
        toast.success(`Cotización ${cotNo} corregida · Revisión ${data.revision}`);
        return;
      }
      await api.post("/cotizaciones", {
        cot_no: cotNo, cliente: selectedCliente.nombre_razon_social, cliente_id: selectedCliente.id,
        vendedor_asignado_id: selectedCliente.vendedor_asignado_id || null, condicion_pago: condicionPago,
        estatus: "Pendiente de autorización", fecha: new Date().toISOString().slice(0, 10), vigencia,
        partidas, descuentos_aplicados: activeDiscounts, subtotal: subtotalConDescuento, iva, total,
      });
      setSaved(true);
      toast.success(`Cotización ${cotNo} guardada`);
    } catch (err) {
      toast.error(`Error al guardar la cotización: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = useReactToPrint({ contentRef: printRef, documentTitle: cotNo || "Cotizacion" });

  const Steps = () => (
    <div className="flex items-center gap-2 mb-6">
      {["Cliente", "Partidas", "Descuentos", "Resumen"].map((label, i) => (
        <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${step === i + 1 ? "bg-primary text-primary-foreground" : step > i + 1 ? "bg-muted text-foreground" : "bg-muted/50 text-muted-foreground"}`}>
          <span className="h-5 w-5 rounded-full bg-background/30 flex items-center justify-center text-xs">{i + 1}</span>{label}
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="cotizador-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{editCot ? `Editar cotización ${editCot.cot_no}` : "Cotizador"}</h1>
        <p className="text-muted-foreground mt-2" data-testid="cot-modo">{editCot ? `Corrige la cotización y guárdala. Se conserva el folio y se guardará como Revisión ${(editCot.revision || 0) + (saved ? 0 : 1)}.` : "Genera una nueva cotización paso a paso."}</p>
      </div>
      <Steps />

      {step === 1 && (
        <Card>
          <CardHeader><CardTitle>1. Cliente y condiciones</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Cliente</Label>
              <Select value={selectedClienteId} onValueChange={setSelectedClienteId} disabled={!!editCot}>
                <SelectTrigger data-testid="cot-cliente"><SelectValue placeholder="Seleccione cliente" /></SelectTrigger>
                <SelectContent>{clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.id_cliente} - {c.nombre_razon_social}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Lista de Precios</Label>
              <Select value={listaPrecios} onValueChange={setListaPrecios}>
                <SelectTrigger data-testid="cot-lista"><SelectValue placeholder="Seleccione lista" /></SelectTrigger>
                <SelectContent>{listas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Condición de Pago</Label>
              <Select value={condicionPago} onValueChange={setCondicionPago}>
                <SelectTrigger data-testid="cot-condicion"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                <SelectContent><SelectItem value="Contado">Contado</SelectItem><SelectItem value="Crédito">Crédito</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Vigencia</Label>
              <Input value={vigencia} onChange={(e) => setVigencia(e.target.value)} />
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader><CardTitle>2. Partidas</CardTitle><CardDescription>Lista: {listas.find((l) => l.id === listaPrecios)?.name || "—"}</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2 items-end">
              <div className="flex-1 space-y-2">
                <Label>Productos</Label>
                <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="cot-producto" className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring">
                      <span className={checkedIds.length ? "" : "text-muted-foreground"}>
                        {checkedIds.length ? `${checkedIds.length} producto(s) seleccionado(s)` : "Seleccione productos"}
                      </span>
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 min-w-[520px] w-[520px] flex flex-col" align="start" side="bottom" sideOffset={4} collisionPadding={12} style={{ maxHeight: "min(420px, var(--radix-popover-content-available-height))" }}>
                    <div className="shrink-0 pl-2 pr-2 border-b border-gray-400" style={{ background: "linear-gradient(90deg, rgb(247,206,206), rgb(252,240,240))" }}>
                      <div className="grid items-center text-xs font-bold uppercase" style={{ gridTemplateColumns: "40px 100px 180px 140px" }}>
                        <span className="py-2 flex items-center justify-center border-r border-gray-400">
                          <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="cot-check-all" aria-label="Seleccionar todos" />
                        </span>
                        <span className="py-2 px-2 border-r border-gray-400">N/REF</span>
                        <span className="py-2 px-2 border-r border-gray-400">Modelo</span>
                        <span className="py-2 pl-2 text-right">Precio Unitario</span>
                      </div>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto">
                      {availableProducts.length === 0 && (
                        <div className="px-3 py-4 text-sm text-muted-foreground text-center">Sin productos en la lista de precios.</div>
                      )}
                      {availableProducts.map((p, i) => {
                        const checked = checkedIds.includes(p.product_id);
                        const yaEnLista = partidas.some((x) => x.product_id === p.product_id);
                        return (
                          <div
                            key={p.product_id}
                            onClick={() => toggleChecked(p.product_id)}
                            data-testid={`cot-producto-row-${p.product_id}`}
                            className={`grid items-center text-sm cursor-pointer border-b border-gray-200 hover:bg-red-50 ${yaEnLista ? "opacity-60" : ""} ${checked ? "bg-red-50" : i % 2 === 1 ? "bg-gray-100" : ""}`}
                            style={{ gridTemplateColumns: "40px 100px 180px 140px" }}
                          >
                            <span className="py-1 flex items-center justify-center border-r border-gray-200">
                              <Checkbox checked={checked} onCheckedChange={() => toggleChecked(p.product_id)} onClick={(e) => e.stopPropagation()} data-testid={`cot-check-${p.product_id}`} />
                            </span>
                            <span className="py-1 px-2 border-r border-gray-200">{p.nRef || "-"}</span>
                            <span className="py-1 px-2 border-r border-gray-200">{p.modelo}{yaEnLista && <span className="ml-1 text-[10px] font-semibold text-red-500">· en lista</span>}</span>
                            <span className="py-1 pl-2 text-right">{Number(p.precioUnitario).toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="shrink-0 flex items-center justify-between gap-2 border-t p-2 bg-white">
                      <span className="text-xs text-muted-foreground">{checkedIds.length} seleccionado(s)</span>
                      <div className="flex gap-2">
                        <Button type="button" variant="ghost" size="sm" onClick={() => setCheckedIds([])} disabled={!checkedIds.length}>Limpiar</Button>
                        <Button type="button" size="sm" onClick={confirmAddSelected} data-testid="cot-add-selected"><Plus className="mr-1 h-4 w-4" /> Agregar productos</Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="border rounded-md overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Ref</TableHead><TableHead>Modelo</TableHead><TableHead>Tipo</TableHead><TableHead>Cantidad</TableHead><TableHead>Atados</TableHead><TableHead>Peso (kg)</TableHead><TableHead>P. Unitario</TableHead><TableHead className="text-right">Subtotal</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {partidas.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="text-center h-24 text-muted-foreground">No hay partidas. Agregue productos.</TableCell></TableRow>
                  ) : partidas.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.nRef || "-"}</TableCell>
                      <TableCell className="font-medium">{p.modelo}</TableCell>
                      <TableCell>
                        <Select value={p.tipo || "Verde"} onValueChange={(v) => updatePartida(p.id, "tipo", v)}>
                          <SelectTrigger className="h-8 w-28" data-testid={`partida-tipo-${p.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Verde">Verde</SelectItem>
                            <SelectItem value="Negra">Negra</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><Input className="h-8 w-20" type="number" min="0" value={p.cantidad} onChange={(e) => updatePartida(p.id, "cantidad", e.target.value)} /></TableCell>
                      <TableCell>{fmtAtados(p.atados)}</TableCell>
                      <TableCell>{Number(p.peso).toFixed(2)}</TableCell>
                      <TableCell>${Number(p.precioUnitario).toFixed(2)}</TableCell>
                      <TableCell className="text-right">${(Number(p.cantidad) * Number(p.precioUnitario)).toFixed(2)}</TableCell>
                      <TableCell><Button variant="ghost" size="icon" className="text-destructive" onClick={() => removePartida(p.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="text-sm text-muted-foreground flex gap-6"><span>Total atados: {fmtAtados(totalAtados)}</span><span>Total kg: {totalKg.toFixed(2)}</span><span className="font-semibold text-foreground">Subtotal: {money(subtotal)}</span></div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardHeader><CardTitle>3. Descuentos</CardTitle><CardDescription>Descuentos pre-asignados al cliente. Puedes suspenderlos para esta cotización.</CardDescription></CardHeader>
          <CardContent>
            <div className="border rounded-md">
              <Table>
                <TableHeader><TableRow><TableHead>Descuento</TableHead><TableHead>Valor</TableHead><TableHead className="text-right">Estado</TableHead></TableRow></TableHeader>
                <TableBody>
                  {descuentos.length === 0 ? (
                    <TableRow><TableCell colSpan={3} className="text-center h-20 text-muted-foreground">El cliente no tiene descuentos asignados.</TableCell></TableRow>
                  ) : descuentos.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-medium">{d.nombre}</TableCell>
                      <TableCell>{d.valor}%</TableCell>
                      <TableCell className="text-right"><div className="flex items-center justify-end gap-2"><span className="text-sm text-muted-foreground">{d.estado === "activo" ? "Activado" : "Desactivado"}</span><Switch checked={d.estado === "activo"} onCheckedChange={(ch) => toggleDescuento(d.id, ch)} /></div></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div><CardTitle>4. Resumen</CardTitle><CardDescription>Folio: {cotNo}</CardDescription></div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { if (editCot) { navigate("/cotizaciones"); return; } setSelectedClienteId(""); setPartidas([]); setDescuentos([]); setCotNo(""); setSaved(false); setSaving(false); setVigencia("15 días"); setCondicionPago(""); setListaPrecios(""); setSelectedProductId(""); setStep(1); }} data-testid="cot-inicio"><Home className="mr-2 h-4 w-4" /> {editCot ? "Volver a cotizaciones" : "Inicio"}</Button>
              <Button variant="outline" onClick={() => downloadCotizacionPdf(bodyRef.current, headerRef.current, cotNo || "Cotizacion")} data-testid="cot-descargar-pdf"><Download className="mr-2 h-4 w-4" /> Descargar PDF</Button>
              <Button variant="outline" onClick={() => printCotizacionPdf(bodyRef.current, headerRef.current)} data-testid="cot-print"><Printer className="mr-2 h-4 w-4" /> Imprimir</Button>
              {!saved ? <Button onClick={guardar} disabled={saving} data-testid="cot-save">{saving ? "Guardando..." : editCot ? "Guardar corrección" : "Guardar cotización"}</Button>
                : <span className="flex items-center gap-2 text-emerald-600 font-medium"><CheckCircle2 className="h-5 w-5" /> Guardada</span>}
            </div>
          </CardHeader>
          <CardContent>
            <div ref={printRef} className="bg-white text-black px-10 py-8 mx-auto" style={{ width: "215.9mm", boxSizing: "border-box" }} data-testid="cot-resumen">
              <style>{`
                @media print{
                  @page{size:letter;margin:12mm}
                  body{-webkit-print-color-adjust:exact;print-color-adjust:exact;background:#fff!important}
                  table{page-break-inside:auto;border-collapse:collapse}
                  thead{display:table-header-group}
                  tr{page-break-inside:avoid;break-inside:avoid}
                  th,td{-webkit-print-color-adjust:exact;print-color-adjust:exact}
                  .avoid-break{page-break-inside:avoid;break-inside:avoid}
                }
              `}</style>
              <div ref={headerRef}>
              <div className="grid grid-cols-[auto_1fr_auto] gap-6 items-start bg-gray-50 px-3 py-3">
                <div className="text-center">
                  <img src="/logo-cooper.png" className="h-16 w-16 object-contain rounded-lg mx-auto" alt="Cooper Slip" />
                  <div className="text-xs mt-1">Cooper Slip</div>
                </div>
                <div className="text-center text-sm font-bold leading-snug pt-3">
                  <p>Cuando tu cliente queda bien, Tu quedas mejor.</p>
                  <p>Elije las duelas que respaldan a tu negocio</p>
                </div>
                <div className="text-right">
                  <h2 className="text-3xl font-extrabold tracking-tight">COTIZACIÓN</h2>
                  <p className="text-sm mt-1"><span className="font-bold">No.:</span> {cotNo}</p>
                  <p className="text-sm"><span className="font-bold">Fecha:</span> {editCot ? fechaMX(editCot.fecha) : fechaMX(new Date())}</p>
                  {editCot && <p className="text-sm" data-testid="cot-revision"><span className="font-bold">Revisión {saved ? editCot.revision : (editCot.revision || 0) + 1}:</span> {fechaMX(saved && editCot.revisado_at ? editCot.revisado_at : new Date())}</p>}
                </div>
              </div>
              <div className="border-t-4 border-black" />
              </div>
              <div ref={bodyRef}>
              <div className="mt-4">
                <h3 className="font-bold text-base mb-1">Datos del Cliente</h3>
                <p className="text-sm"><span className="font-bold">Cliente:</span> {selectedCliente?.nombre_razon_social}</p>
                {selectedCliente?.rfc && <p className="text-sm"><span className="font-bold">RFC:</span> {selectedCliente.rfc}</p>}
              </div>
              <table className="w-full text-xs border-collapse mt-4">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-2 py-1 text-left">N/REF</th>
                    <th className="border border-gray-300 px-2 py-1 text-left">Modelos</th>
                    <th className="border border-gray-300 px-2 py-1 text-center">Cantidad</th>
                    <th className="border border-gray-300 px-2 py-1 text-center">Panel</th>
                    <th className="border border-gray-300 px-2 py-1 text-center">Atados</th>
                    <th className="border border-gray-300 px-2 py-1 text-center">Peso</th>
                    <th className="border border-gray-300 px-2 py-1 text-right">P/U</th>
                    <th className="border border-gray-300 px-2 py-1 text-right">C/Descuento</th>
                    <th className="border border-gray-300 px-2 py-1 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {partidas.map((p) => {
                    const pu = Number(p.precioUnitario) || 0;
                    const pc = pu * cotFactor;
                    const rs = Number(p.cantidad) * pc;
                    return (
                      <tr key={p.id}>
                        <td className="border border-gray-300 px-2 py-1">{p.nRef || "-"}</td>
                        <td className="border border-gray-300 px-2 py-1">{p.modelo}</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">{p.cantidad}</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">{p.tipo || "Verde"}</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">{fmtAtados(p.atados)}</td>
                        <td className="border border-gray-300 px-2 py-1 text-center">{Number(p.peso).toFixed(2)}</td>
                        <td className="border border-gray-300 px-2 py-1 text-right">{dinero(pu)}</td>
                        <td className="border border-gray-300 px-2 py-1 text-right">{dinero(pc)}</td>
                        <td className="border border-gray-300 px-2 py-1 text-right font-semibold">{dinero(rs)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex justify-end mt-5 avoid-break">
                <div className="w-80 text-sm">
                  <div className="flex justify-between py-1"><span>Subtotal:</span><span className="font-bold">{dinero(subtotal)}</span></div>
                  {activeDiscounts.map((d, i) => (
                    <div key={i} className="flex justify-between py-1 text-red-600"><span>Descuento por {d.nombre} ({d.valor}%):</span><span></span></div>
                  ))}
                  {totalDescuento > 0 && <div className="flex justify-between py-1 text-red-600"><span>Total Descuento:</span><span className="font-bold">-{dinero(totalDescuento)}</span></div>}
                  <div className="border-t border-gray-300 mt-1 pt-1 flex justify-between py-1"><span>Subtotal con Descuento:</span><span className="font-bold">{dinero(subtotalConDescuento)}</span></div>
                  <div className="flex justify-between py-1"><span>IVA (16%):</span><span className="font-bold">{dinero(iva)}</span></div>
                  <div className="border-t-2 border-black mt-1 pt-2 flex justify-between items-center"><span className="text-blue-600 font-bold text-xl">Total:</span><span className="text-blue-600 font-bold text-xl">{dinero(total)}</span></div>
                </div>
              </div>
              <p className="text-center italic text-sm mt-6">Con este Importe No solo compras duelas, compras tranquilidad y reputacion frente a tus clientes</p>
              <div className="mt-6 avoid-break">
                <h3 className="font-bold text-sm mb-2">Información Importante:</h3>
                <ul className="list-disc pl-6 text-sm space-y-1">
                  <li><span className="font-bold">Identificador:</span> {identificador}</li>
                  <li><span className="font-bold">Cantidad total de atados:</span> {fmtAtados(totalAtados)}</li>
                  <li><span className="font-bold">Cantidad total de KG:</span> {totalKg.toFixed(2)} kg</li>
                  <li><span className="font-bold">Condiciones de pago:</span> {condicionPago || "-"}</li>
                  <li><span className="font-bold">Tiempo de entrega:</span> {selectedCliente?.tiempo_entrega || "-"}</li>
                  <li><span className="font-bold">Vigencia de cotización:</span> {vigencia || "-"}</li>
                </ul>
              </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}>Anterior</Button>
        {step < 3 && <Button onClick={() => setStep((s) => s + 1)} disabled={step === 1 && !selectedClienteId} data-testid="cot-next">Siguiente</Button>}
        {step === 3 && <Button onClick={goStep4} data-testid="cot-next">Ir al resumen</Button>}
      </div>
    </div>
  );
}
